import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { firstValueFrom } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { BonusApi } from 'src/app/shared/apis/bonus.api';
import { SettingsApi } from 'src/app/shared/apis/settings.api';
import { CSButtonComponent } from 'src/app/shared/components/cs-button/cs-button.component';
import { CSIconButtonComponent } from 'src/app/shared/components/cs-icon-button/cs-icon-button.component';
import { CSTabComponent } from 'src/app/shared/components/cs-tab/cs-tab.component';
import { EmptyModule } from 'src/app/shared/components/empty/empty.module';
import { LoadingModule } from 'src/app/shared/directive/loading/loading.module';
import { AccountInforData } from 'src/app/shared/interfaces/account.interface';
import { HandleInnerHtmlService } from 'src/app/shared/service/handle-inner-html.service';
import { NotificationService } from '../../notification.service';
import { DialogType, NotificationDialogService } from '../notification-dialog.service';

type NoticeType = 'Activity' | 'System' | 'Information' | 'Transaction';
@UntilDestroy()
@Component({
  selector: 'notification-overview-dialog',
  templateUrl: './overview.dialog.html',
  styleUrls: ['./overview.dialog.scss'],
  standalone: true,
  imports: [
    CSButtonComponent,
    CSIconButtonComponent,
    CSTabComponent,
    CommonModule,
    DatePipe,
    LoadingModule,
    EmptyModule,
  ],
})
export class NotificationOverviewDialog implements OnInit {
  logined!: boolean; //是否登陆状态
  tabIndex$ = this.notificationDialogService.tabIndex$; //当前tab索引

  generalList: any[] = []; //常规通知列表

  promotionList: any[] = []; //推广列表

  noticeListCount: number = 0; // 当前有多少信息未读

  loading = false; //加载状态

  filterTypes: NoticeType[] = [];
  private noticeConfigReady: Promise<void>;
  private readonly noticeTypeStorageKey = 'noticeTypeList';

  constructor(
    private notificationService: NotificationService,
    private bounsApi: BonusApi,
    private appService: AppService,
    private router: Router,
    private dialogRef: MatDialogRef<NotificationOverviewDialog>,
    private notificationDialogService: NotificationDialogService,
    private settingsApi: SettingsApi,
    private handleInnerHtmlService: HandleInnerHtmlService,
  ) {
    this.noticeConfigReady = this.loadNoticeConfig();
  }

  ngOnInit(): void {
    this.appService.userInfo$.pipe(untilDestroyed(this)).subscribe((x: AccountInforData | null) => {
      this.logined = !!x;
    });
    this.appService.noticeCounts$.pipe(untilDestroyed(this)).subscribe(async e => {
      this.noticeListCount = e?.All || 0;

      if (this.logined) {
        await this.updateGeneralList();
        await this.updatePromotionList();
      }
      if (this.noticeListCount === 0) {
        this.notificationService.isReadedAll$.next(true);
      } else {
        this.notificationService.isReadedAll$.next(false);
      }
    });
    this.notificationService.reLoadData$.subscribe(async () => {
      if (this.logined) {
        await this.updateGeneralList();
        this.notificationService.getNoticeCounts();
      }
    });
  }

  async updateGeneralList() {
    await this.noticeConfigReady;
    this.loading = true;
    const list: any[] = await firstValueFrom(this.notificationService.getNoticeList());
    const filterTypes = this.filterTypes || [];
    this.generalList = filterTypes.length > 0 ? list.filter(item => filterTypes.includes(item.noticeType)) : list;
    // this.loading = false;
  }

  async updatePromotionList() {
    this.loading = true;
    const activityInfo = await firstValueFrom(this.bounsApi.getActivityInfo({ equipment: 'Web' }));
    this.promotionList = activityInfo.list[0]?.list || [];
    this.loading = false;
  }

  onTabChanged(event: number) {
    this.tabIndex$.next(event);
  }

  onGeneralClick(item: any) {
    this.notificationDialogService.detailTitle = item.title;
    this.notificationDialogService.detailContent = this.handleInnerHtmlService.handleSpecialChar(item.content);
    this.notificationDialogService.detailSendTime = item.sendTime;
    this.notificationDialogService.dialogType$.next(DialogType.DETAIL);

    // 打開的同時標示為已讀
    this.onReadClick(item);
  }

  onPromitionClick(item: any) {
    this.dialogRef.close();
    this.router.navigateByUrl(`/${this.appService.languageCode}/promotions/offer/${item.activitiesNo}`);
  }

  onReadClick(item: any) {
    if (!item?.id) {
      return;
    }
    this.notificationDialogService.onReadMsg(item.id);
  }

  onGeneralWalletClick() {
    this.dialogRef.close();
    this.router.navigateByUrl(`/${this.appService.languageCode}/wallet/overview`);
  }

  readAll() {
    if (this.generalList.length === 0) {
      return;
    }
    this.notificationDialogService.onReadAllMsg(this.generalList.map(item => item.id));
  }

  close() {
    this.dialogRef.close();
  }

  private async loadNoticeConfig() {
    const cachedTypes = this.getCachedNoticeTypes();
    if (cachedTypes.length > 0) {
      this.filterTypes = cachedTypes;
      return;
    }

    try {
      const noticeConfig = await firstValueFrom(this.settingsApi.getNoticeConfig());
      const remoteTypes = noticeConfig.data?.noticeTypeList || [];
      this.filterTypes = remoteTypes;
      this.cacheNoticeTypes(remoteTypes);
    } catch {
      // keep using cached filterTypes if remote load fails
    }
  }

  private getCachedNoticeTypes(): NoticeType[] {
    try {
      const raw = localStorage.getItem(this.noticeTypeStorageKey);
      return raw ? (JSON.parse(raw) as NoticeType[]) : [];
    } catch {
      return [];
    }
  }

  private cacheNoticeTypes(types: NoticeType[]) {
    try {
      localStorage.setItem(this.noticeTypeStorageKey, JSON.stringify(types));
    } catch {
      // ignore storage errors
    }
  }
}
